import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Import new components for AI Reporting Engine
import ReportTemplates from './ReportTemplates';
import ReportExport from './ReportExport';
import DistributionRules from './DistributionRules';
import SemanticModel from './SemanticModel';
import SmtpSettings from './SmtpSettings';
import LlmSettings from './LlmSettings';
import PromptTemplates from './PromptTemplates';
import LogsAudit from './LogsAudit';
import GeneralSettings from './GeneralSettings';
import UserManagement from './UserManagement';

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeTab, setActiveTab] = useState('semantic-model');
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableQueryTypes, setAvailableQueryTypes] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Form states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form data
  const [categoryForm, setCategoryForm] = useState({
    title: '',
    color: 'bg-blue-50 border-blue-100',
    icon_bg: 'bg-blue-500',
    text_color: 'text-blue-700',
    icon_type: 'chart',
    order_index: 0
  });
  
  const [questionForm, setQuestionForm] = useState({
    category_id: '',
    question_text: '',
    order_index: 0
  });
  
  const [cardForm, setCardForm] = useState({
    title: '',
    icon: '🏠',
    description: '',
    gradient: 'from-blue-500 to-cyan-500',
    bg_color: 'bg-blue-50',
    text_color: 'text-blue-600',
    query_type: 'total_properties',
    order_index: 0
  });

  useEffect(() => {
    if (['categories', 'questions', 'cards'].includes(activeTab)) {
      fetchAllData();
      fetchAvailableQueryTypes();
    }
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, questionsRes, cardsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/question-categories`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/suggested-questions`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-cards`)
      ]);
      
      setCategories(categoriesRes.data);
      setQuestions(questionsRes.data);
      setCards(cardsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableQueryTypes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/available-query-types`);
      setAvailableQueryTypes(response.data.query_types || []);
    } catch (error) {
      console.error('Error fetching available query types:', error);
      setAvailableQueryTypes([
        {value: 'total_properties', label: 'Total Properties', description: 'Count of all properties'},
        {value: 'total_auctions', label: 'Total Auctions', description: 'Count of all auctions'},
        {value: 'total_bids', label: 'Total Bids', description: 'Count of all bids'},
        {value: 'total_users', label: 'Total Users', description: 'Count of all users'},
      ]);
    }
  };

  // Category CRUD
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/question-categories`, categoryForm);
      await fetchAllData();
      resetCategoryForm();
      setShowCategoryForm(false);
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error creating category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/question-categories/${editingItem.id}`,
        categoryForm
      );
      await fetchAllData();
      resetCategoryForm();
      setShowCategoryForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Error updating category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure? This will also delete all questions in this category.')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/question-categories/${id}`);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      title: '',
      color: 'bg-blue-50 border-blue-100',
      icon_bg: 'bg-blue-500',
      text_color: 'text-blue-700',
      icon_type: 'chart',
      order_index: 0
    });
  };

  // Question CRUD
  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/suggested-questions`, questionForm);
      await fetchAllData();
      resetQuestionForm();
      setShowQuestionForm(false);
      alert('Question created successfully!');
    } catch (error) {
      console.error('Error creating question:', error);
      alert('Error creating question: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/suggested-questions/${editingItem.id}`,
        questionForm
      );
      await fetchAllData();
      resetQuestionForm();
      setShowQuestionForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating question:', error);
      alert('Error updating question');
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/suggested-questions/${id}`);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Error deleting question');
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      category_id: '',
      question_text: '',
      order_index: 0
    });
  };

  // Card CRUD
  const handleCreateCard = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-cards`, cardForm);
      await fetchAllData();
      resetCardForm();
      setShowCardForm(false);
      alert('Dashboard card created successfully!');
    } catch (error) {
      console.error('Error creating card:', error);
      alert('Error creating card: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateCard = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-cards/${editingItem.id}`,
        cardForm
      );
      await fetchAllData();
      resetCardForm();
      setShowCardForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating card:', error);
      alert('Error updating card');
    }
  };

  const handleDeleteCard = async (id) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard-cards/${id}`);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting card:', error);
      alert('Error deleting card');
    }
  };

  const resetCardForm = () => {
    setCardForm({
      title: '',
      icon: '🏠',
      description: '',
      gradient: 'from-blue-500 to-cyan-500',
      bg_color: 'bg-blue-50',
      text_color: 'text-blue-600',
      query_type: availableQueryTypes.length > 0 ? availableQueryTypes[0].value : '',
      order_index: 0
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation menu configuration
  const menuSections = [
    /* HIDDEN - Report Configuration Section
    {
      title: 'Report Configuration',
      items: [
        { id: 'report-templates', label: 'Report Templates', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )},
        { id: 'report-export', label: 'Report Export', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        )},
        { id: 'distribution-rules', label: 'Distribution Rules', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )},
      ]
    },
    */
    {
      title: 'Data Model',
      items: [
        { id: 'semantic-model', label: 'Semantic Model', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
        )},
      ]
    },
    {
      title: 'Chat Configuration',
      items: [
        { id: 'categories', label: 'Question Categories', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        )},
        { id: 'questions', label: 'Suggested Questions', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )},
        // { id: 'cards', label: 'Dashboard Cards', icon: (
        //   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        //   </svg>
        // )},
      ]
    },
    {
      title: 'Settings',
      items: [
        { id: 'user-management', label: 'User Management', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )},
        { id: 'smtp-settings', label: 'Email / SMTP', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )},
        { id: 'llm-settings', label: 'LLM Model', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )},
        { id: 'prompt-templates', label: 'Prompt Templates', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )},
        { id: 'logs-audit', label: 'Logs & Audit', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )},
        { id: 'general-settings', label: 'General Settings', icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )},
      ]
    },
  ];

  // Get current tab label
  const getCurrentTabLabel = () => {
    for (const section of menuSections) {
      const item = section.items.find(i => i.id === activeTab);
      if (item) return item.label;
    }
    return 'Admin Panel';
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Left Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-56'} bg-slate-900 flex flex-col transition-all duration-300 ease-in-out`}>
        {/* Sidebar Header */}
        <div className="p-3 border-b border-slate-800">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-sm font-bold text-white">Admin Panel</h1>
                  <p className="text-[10px] text-slate-400">Asset Manager</p>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <svg className={`w-4 h-4 text-slate-400 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-4">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {!sidebarCollapsed && (
                <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 px-2">
                  {section.title}
                </h3>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-start'} space-x-2 px-2 py-2 rounded-lg transition-all duration-200 group ${
                      activeTab === item.id
                        ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className={`flex-shrink-0 ${activeTab === item.id ? 'text-emerald-400' : 'text-slate-500 group-hover:text-slate-300'}`}>
                      {React.cloneElement(item.icon, { className: 'w-4 h-4' })}
                    </span>
                    {!sidebarCollapsed && (
                      <span className="font-medium text-xs">{item.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer - User Info */}
        <div className="p-2 border-t border-slate-800">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'space-x-2'}`}>
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-xs">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
                <p className="text-[10px] text-slate-500">Administrator</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="bg-slate-900 border-b border-slate-800 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h2 className="text-sm font-bold text-white">{getCurrentTabLabel()}</h2>
              <p className="text-[10px] text-slate-400">Configure and manage settings</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-red-600/80 rounded-lg transition-all flex items-center space-x-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Logout</span>
          </button>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-hidden">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full m-4 flex flex-col">
            {/* Report Templates Tab */}
            {/* HIDDEN - Report Configuration Tabs
            {activeTab === 'report-templates' && <ReportTemplates />}
            {activeTab === 'report-export' && <ReportExport />}
            {activeTab === 'distribution-rules' && <DistributionRules />}
            */}

            {/* Semantic Model Tab */}
            {activeTab === 'semantic-model' && <SemanticModel />}

            {/* User Management Tab */}
            {activeTab === 'user-management' && <UserManagement />}

            {/* SMTP Settings Tab */}
            {activeTab === 'smtp-settings' && <SmtpSettings />}

            {/* LLM Settings Tab */}
            {activeTab === 'llm-settings' && <LlmSettings />}

            {/* Prompt Templates Tab */}
            {activeTab === 'prompt-templates' && <PromptTemplates />}

            {/* Logs & Audit Tab */}
            {activeTab === 'logs-audit' && <LogsAudit />}

            {/* General Settings Tab */}
            {activeTab === 'general-settings' && <GeneralSettings />}

            {/* Legacy Tabs - Categories */}
            {activeTab === 'categories' && (
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-bold text-slate-900">Question Categories</h2>
                      <button
                        onClick={() => {
                          resetCategoryForm();
                          setEditingItem(null);
                          setShowCategoryForm(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-700 transition-colors shadow-sm"
                      >
                        + Add Category
                      </button>
                    </div>

                    {showCategoryForm && (
                      <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="text-sm font-semibold mb-3">
                          {editingItem ? 'Edit Category' : 'New Category'}
                        </h3>
                        <form onSubmit={editingItem ? handleUpdateCategory : handleCreateCategory}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Title</label>
                              <input
                                type="text"
                                value={categoryForm.title}
                                onChange={(e) => setCategoryForm({...categoryForm, title: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Order Index</label>
                              <input
                                type="number"
                                value={categoryForm.order_index}
                                onChange={(e) => setCategoryForm({...categoryForm, order_index: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Icon Type</label>
                              <select
                                value={categoryForm.icon_type}
                                onChange={(e) => setCategoryForm({...categoryForm, icon_type: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                              >
                                <option value="chart">Chart</option>
                                <option value="users">Users</option>
                                <option value="building">Building</option>
                                <option value="lightning">Lightning</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Color Class</label>
                              <input
                                type="text"
                                value={categoryForm.color}
                                onChange={(e) => setCategoryForm({...categoryForm, color: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                placeholder="bg-blue-50 border-blue-100"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Icon Background</label>
                              <input
                                type="text"
                                value={categoryForm.icon_bg}
                                onChange={(e) => setCategoryForm({...categoryForm, icon_bg: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                placeholder="bg-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Text Color</label>
                              <input
                                type="text"
                                value={categoryForm.text_color}
                                onChange={(e) => setCategoryForm({...categoryForm, text_color: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                placeholder="text-blue-700"
                              />
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-700 transition-colors"
                            >
                              {editingItem ? 'Update' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCategoryForm(false);
                                setEditingItem(null);
                                resetCategoryForm();
                              }}
                              className="px-3 py-1.5 text-xs bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="space-y-2">
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 ${category.icon_bg} rounded-lg flex items-center justify-center`}>
                              <span className="text-white text-xs">#{category.order_index}</span>
                            </div>
                            <div>
                              <h4 className="font-medium text-sm text-slate-900">{category.title}</h4>
                              <p className="text-xs text-slate-500">
                                {category.icon_type} • {category.is_active ? 'Active' : 'Inactive'}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-1.5">
                            <button
                              onClick={() => {
                                setEditingItem(category);
                                setCategoryForm({
                                  title: category.title,
                                  color: category.color,
                                  icon_bg: category.icon_bg,
                                  text_color: category.text_color,
                                  icon_type: category.icon_type,
                                  order_index: category.order_index
                                });
                                setShowCategoryForm(true);
                              }}
                              className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                      {categories.length === 0 && (
                        <p className="text-center text-slate-500 text-sm py-6">No categories yet. Create one to get started!</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Legacy Tabs - Questions */}
            {activeTab === 'questions' && (
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-bold text-slate-900">Suggested Questions</h2>
                      <button
                        onClick={() => {
                          resetQuestionForm();
                          setEditingItem(null);
                          setShowQuestionForm(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-700 transition-colors shadow-sm"
                        disabled={categories.length === 0}
                      >
                        + Add Question
                      </button>
                    </div>

                    {categories.length === 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-amber-800 text-xs">Please create at least one category first before adding questions.</p>
                      </div>
                    )}

                    {showQuestionForm && (
                      <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="text-sm font-semibold mb-3">
                          {editingItem ? 'Edit Question' : 'New Question'}
                        </h3>
                        <form onSubmit={editingItem ? handleUpdateQuestion : handleCreateQuestion}>
                          <div className="grid grid-cols-1 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
                              <select
                                value={questionForm.category_id}
                                onChange={(e) => setQuestionForm({...questionForm, category_id: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                              >
                                <option value="">Select a category</option>
                                {categories.map((cat) => (
                                  <option key={cat.id} value={cat.id}>{cat.title}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Question Text</label>
                              <textarea
                                value={questionForm.question_text}
                                onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                rows="2"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Order Index</label>
                              <input
                                type="number"
                                value={questionForm.order_index}
                                onChange={(e) => setQuestionForm({...questionForm, order_index: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                              />
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-700 transition-colors"
                            >
                              {editingItem ? 'Update' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowQuestionForm(false);
                                setEditingItem(null);
                                resetQuestionForm();
                              }}
                              className="px-3 py-1.5 text-xs bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="space-y-2">
                      {questions.map((question) => {
                        const category = categories.find(c => c.id === question.category_id);
                        return (
                          <div
                            key={question.id}
                            className="flex items-start justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm text-slate-900">{question.question_text}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Category: <span className="font-medium">{category?.title || 'Unknown'}</span> • 
                                Order: {question.order_index} • 
                                {question.is_active ? 'Active' : 'Inactive'}
                              </p>
                            </div>
                            <div className="flex space-x-1.5 ml-3">
                              <button
                                onClick={() => {
                                  setEditingItem(question);
                                  setQuestionForm({
                                    category_id: question.category_id,
                                    question_text: question.question_text,
                                    order_index: question.order_index
                                  });
                                  setShowQuestionForm(true);
                                }}
                                className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteQuestion(question.id)}
                                className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {questions.length === 0 && (
                        <p className="text-center text-slate-500 text-sm py-6">No questions yet. Create one to get started!</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Legacy Tabs - Dashboard Cards */}
            {activeTab === 'cards' && (
              <div className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-bold text-slate-900">Dashboard Cards</h2>
                      <button
                        onClick={() => {
                          resetCardForm();
                          setEditingItem(null);
                          setShowCardForm(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-700 transition-colors shadow-sm"
                      >
                        + Add Card
                      </button>
                    </div>

                    {showCardForm && (
                      <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="text-sm font-semibold mb-3">
                          {editingItem ? 'Edit Card' : 'New Card'}
                        </h3>
                        <form onSubmit={editingItem ? handleUpdateCard : handleCreateCard}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Title</label>
                              <input
                                type="text"
                                value={cardForm.title}
                                onChange={(e) => setCardForm({...cardForm, title: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Icon (Emoji)</label>
                              <input
                                type="text"
                                value={cardForm.icon}
                                onChange={(e) => setCardForm({...cardForm, icon: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                placeholder="🏠"
                                required
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
                              <input
                                type="text"
                                value={cardForm.description}
                                onChange={(e) => setCardForm({...cardForm, description: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Query Type</label>
                              <select
                                value={cardForm.query_type}
                                onChange={(e) => setCardForm({...cardForm, query_type: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                required
                              >
                                <option value="">Select query type</option>
                                {availableQueryTypes.map((queryType) => (
                                  <option key={queryType.value} value={queryType.value}>
                                    {queryType.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Order Index</label>
                              <input
                                type="number"
                                value={cardForm.order_index}
                                onChange={(e) => setCardForm({...cardForm, order_index: parseInt(e.target.value)})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Background Color</label>
                              <input
                                type="text"
                                value={cardForm.bg_color}
                                onChange={(e) => setCardForm({...cardForm, bg_color: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                placeholder="bg-blue-50"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">Text Color</label>
                              <input
                                type="text"
                                value={cardForm.text_color}
                                onChange={(e) => setCardForm({...cardForm, text_color: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                placeholder="text-blue-600"
                              />
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              className="px-3 py-1.5 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-700 transition-colors"
                            >
                              {editingItem ? 'Update' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCardForm(false);
                                setEditingItem(null);
                                resetCardForm();
                              }}
                              className="px-3 py-1.5 text-xs bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {cards.map((card) => (
                        <div
                          key={card.id}
                          className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className={`w-10 h-10 ${card.bg_color} rounded-lg flex items-center justify-center`}>
                              <span className="text-xl">{card.icon}</span>
                            </div>
                            <span className="text-[10px] font-medium text-slate-500">#{card.order_index}</span>
                          </div>
                          <h4 className="font-medium text-sm text-slate-900 mb-0.5">{card.title}</h4>
                          <p className="text-xs text-slate-500 mb-2">{card.description}</p>
                          <p className="text-[10px] text-slate-400 mb-2">
                            Query: <span className="font-mono">{card.query_type}</span>
                          </p>
                          <div className="flex space-x-1.5">
                            <button
                              onClick={() => {
                                setEditingItem(card);
                                setCardForm({
                                  title: card.title,
                                  icon: card.icon,
                                  description: card.description,
                                  gradient: card.gradient,
                                  bg_color: card.bg_color,
                                  text_color: card.text_color,
                                  query_type: card.query_type,
                                  order_index: card.order_index
                                });
                                setShowCardForm(true);
                              }}
                              className="flex-1 px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCard(card.id)}
                              className="flex-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                      {cards.length === 0 && (
                        <div className="col-span-full">
                          <p className="text-center text-slate-500 text-sm py-6">No dashboard cards yet. Create one to get started!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
